import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Divider
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import apiService from '../../services/api';
import UbigeoSelector from '../../components/forms/UbigeoSelector';
import DireccionForm from '../../components/forms/DireccionForm';
import HorarioEditor from '../../components/forms/HorarioEditor';
import { 
  ComercioExtendido, 
  ComercioExtendidoInput, 
  Direccion, 
  Horario, 
  HORARIO_DEFAULT 
} from '../../types/comercio';

const steps = ['Datos Básicos', 'Dirección Principal', 'Horario de Atención'];

const ComercioForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  // Estados principales
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [activeStep, setActiveStep] = useState(0);

  // Datos del formulario
  const [formData, setFormData] = useState<ComercioExtendidoInput>({
    ruc: '',
    razon_social: '',
    email: '',
    telefono: '',
    password: '',
    direccion_principal: {
      calle: '',
      numero: '',
      departamento_id: '',
      provincia_id: '',
      distrito_id: '',
      referencia: ''
    },
    horario_atencion: HORARIO_DEFAULT
  });

  const [comercio, setComercio] = useState<ComercioExtendido | null>(null);

  useEffect(() => {
    if (isEdit && id) {
      loadComercio(parseInt(id));
    }
  }, [isEdit, id]);

  const loadComercio = async (comercioId: number) => {
    setLoading(true);
    try {
      const response = await apiService.getComercio(comercioId);
      if (response.success) {
        const comercioData = response.data;
        setComercio(comercioData);
        setFormData({
          ruc: comercioData.ruc || '',
          razon_social: comercioData.razon_social || '',
          email: comercioData.email || '',
          telefono: comercioData.telefono || '',
          direccion_principal: {
            calle: comercioData.direccion_calle || comercioData.direccion_principal?.calle || '',
            numero: comercioData.direccion_numero || comercioData.direccion_principal?.numero || '',
            departamento_id: comercioData.direccion_departamento_id || comercioData.direccion_principal?.departamento_id || '',
            provincia_id: comercioData.direccion_provincia_id || comercioData.direccion_principal?.provincia_id || '',
            distrito_id: comercioData.direccion_distrito_id || comercioData.direccion_principal?.distrito_id || '',
            referencia: comercioData.direccion_referencia || comercioData.direccion_principal?.referencia || ''
          },
          horario_atencion: comercioData.horario_atencion || HORARIO_DEFAULT
        });
      } else {
        setError(response.message || 'Error cargando comercio');
      }
    } catch (error) {
      console.error('Error cargando comercio:', error);
      setError('Error cargando comercio');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      let response;
      if (isEdit && id) {
        const { password, ...updateData } = formData;
        response = await apiService.updateComercio(parseInt(id), updateData);
      } else {
        response = await apiService.createComercio(formData);
      }

      if (response.success) {
        navigate('/admin/comercios-ext');
      } else {
        setError(response.message || `Error ${isEdit ? 'actualizando' : 'creando'} comercio`);
      }
    } catch (error) {
      console.error(`Error ${isEdit ? 'actualizando' : 'creando'} comercio:`, error);
      setError(`Error ${isEdit ? 'actualizando' : 'creando'} comercio`);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 0:
        return !!(formData.ruc && formData.razon_social && formData.email);
      case 1:
        return !!(
          formData.direccion_principal.calle &&
          formData.direccion_principal.departamento_id &&
          formData.direccion_principal.provincia_id &&
          formData.direccion_principal.distrito_id
        );
      case 2:
        return true; // Horario siempre válido
      default:
        return false;
    }
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6">Información Básica</Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="RUC *"
                  value={formData.ruc}
                  onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                  disabled={isEdit}
                  helperText={isEdit ? 'El RUC no puede modificarse' : 'Ingrese el RUC de 11 dígitos'}
                  sx={{ flex: '1 1 300px' }}
                />

                <TextField
                  label="Email *"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  sx={{ flex: '1 1 300px' }}
                />
              </Box>

              <TextField
                fullWidth
                label="Razón Social *"
                value={formData.razon_social}
                onChange={(e) => setFormData({ ...formData, razon_social: e.target.value })}
              />

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Teléfono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  sx={{ flex: '1 1 300px' }}
                />

                {!isEdit && (
                  <TextField
                    label="Contraseña temporal"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    helperText="Si no se especifica, se asignará 'temporal123'"
                    sx={{ flex: '1 1 300px' }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h6">Dirección Principal</Typography>
            
            <DireccionForm
              value={formData.direccion_principal}
              onChange={(direccion) => setFormData({ ...formData, direccion_principal: direccion })}
            />
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <HorarioEditor
              value={formData.horario_atencion}
              onChange={(horario) => setFormData({ ...formData, horario_atencion: horario })}
              title="Horario de Atención del Negocio"
              subtitle="Este será el horario base que pueden heredar los locales"
            />
          </Box>
        );

      default:
        return null;
    }
  };

  if (loading && isEdit) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Layout>
      <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/admin/comercios')}
          sx={{ mr: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h4" component="h1">
          {isEdit ? `Editar Comercio: ${comercio?.razon_social}` : 'Nuevo Comercio'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stepper */}
      <Paper sx={{ p: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {/* Contenido del paso */}
        <Box sx={{ minHeight: 400 }}>
          {renderStepContent(activeStep)}
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Botones de navegación */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Anterior
          </Button>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSubmit}
                disabled={loading || !isStepValid(activeStep)}
              >
                {loading ? 'Guardando...' : isEdit ? 'Actualizar' : 'Crear Comercio'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepValid(activeStep)}
              >
                Siguiente
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
      </Box>
    </Layout>
  );
};

export default ComercioForm;
